/**
 * 1.68 (196) — printing a table's check (spec G13–G18). Every printing is a row of `table_check_prints`, claimed
 * before it is printed (`table_check_claim`), finished with its secret token (`table_check_print_done`), read back by
 * the paper's own code (`table_check_find`). Here are the pure rules the till and the waiter's phone share: the code's
 * shape, which waiter request a till answers by itself, what the waiter is told, and the priced snapshot the database
 * checks and the till prints.
 */
import type { Product } from './mock-data'
import type { Promotion } from './promotions'
import { billMoney, sameInstant, type Bill } from './tables'
// one rule with the till's tile (lib/tables): what the waiter is told, and when a print counts as failed
export { PRINT_STUCK_MS, printState } from './tables'

/** The paper's code: DIGITS ONLY — `9918` and nine digits. A wedge scanner types keys, and under the Georgian layout a
 *  Latin letter arrives as a Georgian one; digits never change (the club card's lesson, lib/club.ts: 9917). 99 is GS1's
 *  coupon prefix, so no product's EAN-13 begins with it. */
export const CHECK_PREFIX = '9918'
export const CHECK_CODE = /^9918\d{9}$/
export const isCheckCode = (s: string): boolean => CHECK_CODE.test(s)

/** A till answers a waiter's request by itself only while it is this fresh; an older one waits, amber, for the cashier. */
export const AUTO_ANSWER_MIN = 10

export type CheckSnapshot = {
  lines: { product_id: string; name: string; qty: number; unit_price: number; amount: number }[]
  subtotal: number
  service_bp: number
  service: number
  total: number
}

export type CheckPrint = {
  id: string
  orderId: string
  code: string
  mode: 'auto' | 'answer' | 'new' | 'copy'
  state: 'claimed' | 'sent' | 'failed'
  requestAt: string
  claimedAt: string
  stateAt: string | null
  total: number
  snapshot: CheckSnapshot
}

/** The bill as its paper shows it — the same figures the database checks (`private.table_snapshot_check`). */
export function checkSnapshot(bill: Bill, products: readonly Product[], promotions: readonly Promotion[], serviceProductId: string | null): CheckSnapshot {
  const m = billMoney(bill, products, promotions, serviceProductId)
  return {
    lines: m.rows.map((r) => ({ product_id: r.product_id, name: r.name, qty: r.qty, unit_price: r.price, amount: r.amount })),
    subtotal: m.subtotal,
    service_bp: bill.service_bp,
    service: m.service,
    total: m.total,
  }
}

/** A waiter's request this till may print by itself: asked, at most AUTO_ANSWER_MIN old, not yet claimed by anyone. */
export function autoAnswerable(bill: Bill, now: Date): boolean {
  const req = bill.check_requested_at
  if (!req) return false
  const age = now.getTime() - Date.parse(req)
  if (Number.isNaN(age) || age > AUTO_ANSWER_MIN * 60_000) return false
  const lp = bill.last_print
  return !(lp && sameInstant(lp.requestAt, req))
}

const num = (v: unknown) => (v == null ? 0 : Number(v))

export function mapCheckPrint(r: any): CheckPrint | null {
  if (!r || typeof r !== 'object' || !r.id) return null
  const s = r.snapshot ?? {}
  return {
    id: String(r.id), orderId: String(r.order_id ?? ''), code: String(r.code ?? ''),
    mode: r.mode === 'answer' || r.mode === 'new' || r.mode === 'copy' ? r.mode : 'auto',
    state: r.state === 'sent' || r.state === 'failed' ? r.state : 'claimed',
    requestAt: String(r.request_at ?? ''), claimedAt: String(r.claimed_at ?? ''), stateAt: r.state_at ?? null,
    total: num(r.total),
    snapshot: {
      lines: Array.isArray(s.lines) ? s.lines.map((l: any) => ({
        product_id: String(l.product_id ?? ''), name: String(l.name ?? ''), qty: num(l.qty), unit_price: num(l.unit_price), amount: num(l.amount),
      })) : [],
      subtotal: num(s.subtotal), service_bp: num(s.service_bp), service: num(s.service), total: num(s.total),
    },
  }
}

/** The code as a CODE128 SVG, drawn in the browser (`jsbarcode` only now — the cold path); '' when it cannot be drawn. */
export async function checkBarcodeSvg(code: string): Promise<string> {
  try {
    const { default: JsBarcode } = await import('jsbarcode')
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg')
    JsBarcode(svg, code, { format: 'CODE128', width: 2, height: 44, margin: 0, displayValue: false })
    return svg.outerHTML
  } catch {
    return ''
  }
}
